import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AddOnsAdminApi } from '../adminApi.js'
import type { FacilitiesAdminApi } from '../../facilities/index.js'
import type { ClientAddOn, FeatureActivation, FeatureLicense, LicenseKind, UpsertLicenseInput } from '../index.js'
import { Switch, Skeleton, useFeatures, type LicenseSettingField } from '../../core/index.js'
import { EndpointHint } from '../../core/index.js'
import { Plus, Save } from 'lucide-react'
import { Button } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = AddOnsAdminApi & FacilitiesAdminApi

const INPUT_CLASS = 'w-full rounded border border-marine/30 bg-white px-2 py-1 text-xs text-ink focus:outline-none focus:border-marine'
const KIND_LABELS: Record<LicenseKind, string> = { paid: 'Paid', trial: 'Trial', demo: 'Demo' }
const STATUS_STYLES = { active: 'bg-green-lt text-green', suspended: 'bg-gold-lt text-gold', ended: 'bg-silver-lt text-silver' } as const
const FACILITIES_SHOWN = 50

function formatDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

interface LicenseDraft {
  kind: LicenseKind
  termStart: string
  termEnd: string
  noticeDays: string
  agreementRef: string
  listPrice: string
  currency: string
  facilityLimit: string
  note: string
  /** The add-on's own settings for the client, each as typed; blank follows the deployment's default. */
  settings: Record<string, string>
}

function draftFrom(license: FeatureLicense | null, isDemo: boolean): LicenseDraft {
  return {
    kind: license?.kind ?? (isDemo ? 'demo' : 'paid'),
    termStart: license?.termStart ?? today(),
    termEnd: license?.termEnd ?? '',
    noticeDays: String(license?.noticeDays ?? 30),
    agreementRef: license?.agreementRef ?? '',
    listPrice: license?.billing.listPrice === null || license?.billing.listPrice === undefined ? '' : String(license.billing.listPrice),
    currency: license?.billing.currency ?? 'USD',
    facilityLimit: license?.facilityLimit === null || license?.facilityLimit === undefined ? '' : String(license.facilityLimit),
    note: license?.billing.note ?? '',
    settings: Object.fromEntries(Object.entries(license?.settings ?? {}).map(([key, value]) => [key, String(value)])),
  }
}

/** The settings block as the API takes it: numbers and switches from the typed fields the add-on declares; null when every field is blank. */
function settingsFrom(draft: Record<string, string>, fields: readonly LicenseSettingField[]): Record<string, number | boolean> | null {
  const settings: Record<string, number | boolean> = {}
  for (const field of fields) {
    const raw = (draft[field.key] ?? '').trim()
    if (raw === '') {
      continue
    }
    if (field.kind === 'boolean') {
      settings[field.key] = raw === 'true' || raw === 'on' || raw === 'yes'
    } else if (Number.isFinite(Number(raw))) {
      settings[field.key] = Number(raw)
    }
  }
  return Object.keys(settings).length === 0 ? null : settings
}

function inputFrom(draft: LicenseDraft, version: number | undefined, fields: readonly LicenseSettingField[]): UpsertLicenseInput {
  return {
    ...(fields.length > 0 ? { settings: settingsFrom(draft.settings, fields) } : {}),
    kind: draft.kind,
    termStart: draft.termStart,
    termEnd: draft.termEnd.trim() === '' ? null : draft.termEnd,
    noticeDays: Number(draft.noticeDays),
    agreementRef: draft.agreementRef.trim() === '' ? null : draft.agreementRef.trim(),
    billing: {
      listPrice: draft.listPrice.trim() === '' ? null : Number(draft.listPrice),
      currency: draft.currency.trim() === '' ? null : draft.currency.trim(),
      note: draft.note.trim() === '' ? null : draft.note.trim(),
    },
    facilityLimit: draft.facilityLimit.trim() === '' ? null : Number(draft.facilityLimit),
    ...(version === undefined ? {} : { version }),
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-0.5">{label}</span>
      {children}
    </label>
  )
}

function LicenseForm({ license, isDemo, settingFields, onSave, onCancel, isSaving, error }: { license: FeatureLicense | null; isDemo: boolean; settingFields: readonly LicenseSettingField[]; onSave: (input: UpsertLicenseInput) => void; onCancel: () => void; isSaving: boolean; error: string | null }) {
  const [draft, setDraft] = useState<LicenseDraft>(() => draftFrom(license, isDemo))
  const set = (field: keyof LicenseDraft) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setDraft({ ...draft, [field]: event.target.value })
  const setSetting = (key: string, value: string) => setDraft({ ...draft, settings: { ...draft.settings, [key]: value } })
  return (
    <form
      className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 bg-marine-lt/30 border border-marine/15 rounded p-3"
      onSubmit={(event) => {
        event.preventDefault()
        onSave(inputFrom(draft, license?.version, settingFields))
      }}
    >
      <Field label="Kind">
        <select className={INPUT_CLASS} value={draft.kind} onChange={set('kind')} disabled={isDemo}>
          {(Object.keys(KIND_LABELS) as LicenseKind[]).map((kind) => (
            <option key={kind} value={kind}>
              {KIND_LABELS[kind]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Term start">
        <input className={INPUT_CLASS} type="date" value={draft.termStart} onChange={set('termStart')} required />
      </Field>
      <Field label="Term end (blank: open)">
        <input className={INPUT_CLASS} type="date" value={draft.termEnd} onChange={set('termEnd')} />
      </Field>
      <Field label="Notice days">
        <input className={INPUT_CLASS} type="number" min={0} max={365} value={draft.noticeDays} onChange={set('noticeDays')} />
      </Field>
      <Field label="Agreement ref">
        <input className={INPUT_CLASS} value={draft.agreementRef} onChange={set('agreementRef')} maxLength={120} />
      </Field>
      <Field label="List price">
        <input className={INPUT_CLASS} type="number" min={0} step="0.01" value={draft.listPrice} onChange={set('listPrice')} />
      </Field>
      <Field label="Currency">
        <input className={INPUT_CLASS} value={draft.currency} onChange={set('currency')} maxLength={3} />
      </Field>
      <Field label="Facility limit (blank: none)">
        <input className={INPUT_CLASS} type="number" min={1} value={draft.facilityLimit} onChange={set('facilityLimit')} />
      </Field>
      <div className="col-span-2 sm:col-span-4">
        <Field label="Billing note">
          <input className={INPUT_CLASS} value={draft.note} onChange={set('note')} maxLength={500} />
        </Field>
      </div>
      {settingFields.length > 0 && (
        <div className="col-span-2 sm:col-span-4">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">The client's own limits (blank: the deployment's default, shown; a limit may only be tightened)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {settingFields.map((field) => (
              <Field key={field.key} label={field.label}>
                {field.kind === 'boolean' ? (
                  <select className={INPUT_CLASS} value={draft.settings[field.key] ?? ''} onChange={(event) => setSetting(field.key, event.target.value)}>
                    <option value="">Default ({field.defaultHint})</option>
                    <option value="true">On</option>
                    <option value="false">Off</option>
                  </select>
                ) : (
                  <input className={INPUT_CLASS} type="number" min={0} placeholder={field.defaultHint} value={draft.settings[field.key] ?? ''} onChange={(event) => setSetting(field.key, event.target.value)} />
                )}
              </Field>
            ))}
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="col-span-2 sm:col-span-4 text-[11px] font-bold text-coral">
          {error}
        </p>
      )}
      <div className="col-span-2 sm:col-span-4 flex items-center gap-3">
        <Button type="submit" icon={license ? Save : Plus} size="sm" disabled={isSaving}>
          {isSaving ? 'Saving…' : license ? 'Save license' : 'Create license'}
        </Button>
        <button type="button" onClick={onCancel} disabled={isSaving} className="text-xs font-bold text-ink-mid hover:text-ink">
          Cancel
        </button>
        {isDemo && <span className="text-[11px] text-ink-mid">A demo client gets a demo license, free of charge.</span>}
      </div>
    </form>
  )
}

function LicenseSummary({ license, facilitiesOn }: { license: FeatureLicense; facilitiesOn: number }) {
  return (
    <div className="text-[11px] text-ink-mid">
      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide mr-1.5 ${STATUS_STYLES[license.status]}`}>{license.status}</span>
      {KIND_LABELS[license.kind]} · from {formatDate(license.termStart)}
      {license.termEnd ? ` to ${formatDate(license.termEnd)}` : ', open-ended'} · {license.noticeDays} days notice
      {license.agreementRef ? ` · ${license.agreementRef}` : ''}
      {license.billing.listPrice !== null ? ` · ${license.billing.listPrice} ${license.billing.currency ?? ''} per ${license.billing.unit.replace(/_/g, ' ')}` : ''}
      {' · '}
      {license.facilityLimit === null ? `${facilitiesOn} on` : `${facilitiesOn} of ${license.facilityLimit} on`}
      <span className="block mt-0.5">
        Recorded by {license.grantedBy.sub} on {formatDate(license.grantedAt)}; version {license.version}
        {license.history.length > 1 ? `; last change ${license.history[license.history.length - 1]?.action} on ${formatDate(license.updatedAt)}` : ''}
      </span>
    </div>
  )
}

/** The client's facilities with this add-on's switch beside each: the first page by name, narrowed by a search. */
function FacilitySwitches({ api, clientId, addOn, isLicensed }: { api: AdminApi; clientId: string; addOn: ClientAddOn; isLicensed: boolean }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const facilities = useQuery({
    queryKey: ['admin', 'add-ons', 'facilities', clientId, search],
    queryFn: () => api.listFacilitiesPage(clientId, { limit: FACILITIES_SHOWN, cursor: null, ...(search.trim() === '' ? {} : { q: search.trim() }) }),
  })
  const toggle = useMutation({
    mutationFn: ({ facilityId, enabled }: { facilityId: string; enabled: boolean }) => api.setFacilityAddOn(clientId, facilityId, addOn.key, enabled),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'add-ons', clientId] }),
  })
  const byFacility = new Map<string, FeatureActivation>(addOn.activations.map((activation) => [activation.facilityId, activation]))

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Facilities</div>
        <input className={`${INPUT_CLASS} max-w-[200px]`} placeholder="Search facilities" value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>
      {facilities.isPending ? (
        <Skeleton />
      ) : facilities.isError ? (
        <LoadFailure size="xs" message={facilities.error.message} onRetry={() => void facilities.refetch()} retrying={facilities.isFetching} />
      ) : (
        <ul className="divide-y divide-marine/10 border border-marine/15 rounded">
          {facilities.data.items.map((facility) => {
            const activation = byFacility.get(facility.facilityId)
            return (
              <li key={facility.facilityId} className="flex items-center justify-between gap-3 px-3 py-1.5">
                <div className="min-w-0">
                  <div className="text-xs font-bold text-ink truncate">{facility.name}</div>
                  {activation && (
                    <div className="text-[10px] text-ink-mid">
                      {activation.enabled ? 'On' : 'Off'} since {formatDate(activation.changedAt)} by {activation.changedBy.name ?? activation.changedBy.sub}
                    </div>
                  )}
                </div>
                <Switch label={`${addOn.name} at ${facility.name}`} checked={activation?.enabled ?? false} disabled={toggle.isPending || (!activation?.enabled && !isLicensed)} onChange={(enabled) => toggle.mutate({ facilityId: facility.facilityId, enabled })} />
              </li>
            )
          })}
          {facilities.data.items.length === 0 && <li className="px-3 py-2 text-xs text-ink-mid">No facilities match.</li>}
        </ul>
      )}
      {facilities.data?.nextCursor && <div className="text-[10px] text-ink-mid mt-1">Showing the first {FACILITIES_SHOWN}; search to find another.</div>}
      {toggle.isError && (
        <p role="alert" className="text-[11px] font-bold text-coral mt-1">
          {toggle.error.message}
        </p>
      )}
    </div>
  )
}

function AddOnRow({ api, clientId, addOn, isDemo }: { api: AdminApi; clientId: string; addOn: ClientAddOn; isDemo: boolean }) {
  const queryClient = useQueryClient()
  // The settings the add-on lets the admin set per client, as it declares them in the registry.
  const settingFields = useFeatures().find((feature) => feature.key === addOn.key)?.licenseSettings ?? []
  const [isEditing, setIsEditing] = useState(false)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'add-ons', clientId] })
  const save = useMutation({
    mutationFn: (input: UpsertLicenseInput) => api.upsertLicense(clientId, addOn.key, input),
    onSuccess: async () => {
      await refresh()
      setIsEditing(false)
    },
  })
  const act = useMutation({
    mutationFn: (action: 'suspend' | 'resume' | 'end') => api.licenseAction(clientId, addOn.key, action, {}),
    onSuccess: () => void refresh(),
  })
  const license = addOn.license
  const isInForce = license?.status === 'active'

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-ink">{addOn.name}</span>
            <span className="font-mono text-[10px] text-ink-mid">{addOn.key}</span>
            {!addOn.released && <span className="text-[9px] font-bold uppercase tracking-wide bg-silver-lt text-silver px-1.5 py-0.5 rounded">Not released yet</span>}
          </div>
          <div className="text-[11px] text-ink-mid mt-0.5">{addOn.description}</div>
          <div className="mt-1">{license ? <LicenseSummary license={license} facilitiesOn={addOn.facilitiesOn} /> : <span className="text-[11px] text-ink-mid">No license.</span>}</div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {!isEditing && (
            <button type="button" onClick={() => setIsEditing(true)} className="text-xs font-bold text-marine hover:text-marine-dk">
              {license ? 'Edit' : 'New license'}
            </button>
          )}
          {license?.status === 'active' && (
            <button type="button" disabled={act.isPending} onClick={() => act.mutate('suspend')} className="text-xs font-bold text-ink-mid hover:text-ink">
              Suspend
            </button>
          )}
          {license?.status === 'suspended' && (
            <button type="button" disabled={act.isPending} onClick={() => act.mutate('resume')} className="text-xs font-bold text-marine hover:text-marine-dk">
              Resume
            </button>
          )}
          {license && license.status !== 'ended' && (
            <button type="button" disabled={act.isPending} onClick={() => act.mutate('end')} className="text-xs font-bold text-coral hover:text-coral/80">
              End
            </button>
          )}
        </div>
      </div>
      {act.isError && (
        <p role="alert" className="text-[11px] font-bold text-coral mt-1">
          {act.error.message}
        </p>
      )}
      {isEditing && <LicenseForm license={license} isDemo={isDemo} settingFields={settingFields} isSaving={save.isPending} error={save.isError ? save.error.message : null} onSave={(input) => save.mutate(input)} onCancel={() => setIsEditing(false)} />}
      {license && <FacilitySwitches api={api} clientId={clientId} addOn={addOn} isLicensed={isInForce} />}
    </li>
  )
}

/**
 * The client's add-ons (docs/plans/feature-licensing-plan.md section 5):
 * one row per catalog entry with the license as recorded, the actions on
 * it, and the switch per facility. A sales login sees the same for its
 * demos; the API records its licenses as demo ones.
 */
export function ClientAddOns({ api, clientId, isDemo }: { api: AdminApi; clientId: string; isDemo: boolean }) {
  const addOns = useQuery({ queryKey: ['admin', 'add-ons', clientId], queryFn: () => api.clientAddOns(clientId) })

  return (
    <div className="border border-marine/15 bg-white p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[11px] text-ink-mid">Licensed features beyond the core, switched on per facility. Usage is billed for the facilities that are on.</p>
        <EndpointHint>GET /admin/clients/{'{clientId}'}/add-ons</EndpointHint>
      </div>
      {addOns.isPending ? (
        <Skeleton className="mt-2" />
      ) : addOns.isError ? (
        <div role="alert" className="text-xs font-bold text-coral mt-2">
          {addOns.error.message}
        </div>
      ) : addOns.data.length === 0 ? (
        <div className="text-xs text-ink-mid mt-2">No add-ons in the catalog yet.</div>
      ) : (
        <ul className="divide-y divide-marine/10">
          {addOns.data.map((addOn) => (
            <AddOnRow key={addOn.key} api={api} clientId={clientId} addOn={addOn} isDemo={isDemo} />
          ))}
        </ul>
      )}
    </div>
  )
}
